/**
 * Bulk Resend Modal Component
 * ===========================
 *
 * Admin-only modal for resending emails for multiple form entries at once.
 * Accepts a JSON array of form entry ids, lets the admin choose the delivery
 * mode (PDF attachment or form content in the email body), then processes
 * the entries one at a time against the email endpoints.
 *
 * For each id the component mirrors what the form view page does:
 * 1. Fetches the full form entry (data + resolved attachments)
 * 2. Builds the form model and metadata from the form registry
 * 3. Generates the form HTML via FormRendererService (placeholder mode)
 * 4. Categorizes attachment blobs into images and PDFs
 * 5. Sends the request to the matching email endpoint
 *
 * Progress is reported with a progress bar plus a per-entry status list
 * showing which entries are pending, processing, successful, or failed.
 *
 * Emails are sent to the standard recipients configured for each form type.
 *
 * @author MacSafety Development Team
 * @version 1.0
 * @access Admin Only
 */

import { Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { FormService } from '../../../../services/form.service';
import { EmailService } from '../../../../services/email.service';
import { FormRendererService } from '../../../../services/form-render.service';
import { ToastService } from '../../../../services/toast.service';
import { getFormModel } from '../../../../utils/form-model-map';
import { FORM_REGISTRY } from '../../../../models/form-registry';
import { isPdf, isImage } from '../../../../utils/file-type';
import { ResolvedAttachments, AttachmentWithUrl } from '../../../../models/forms/attachments/resolved-attachments';
import { ManualEmailHtmlRequest, ManualEmailWithPdfRequest } from '../../../../models/email-request';

/** How the resent emails deliver the form: as a PDF attachment or rendered in the email body */
type BulkResendMode = 'pdf' | 'html';

/** Processing status of a single entry in the bulk run */
type BulkItemStatus = 'pending' | 'processing' | 'success' | 'failed';

/** Tracks the progress of a single form entry in the bulk run */
interface BulkResendItem {
  id: string;
  status: BulkItemStatus;
  error?: string;
}

@Component({
  selector: 'app-bulk-resend-modal',
  templateUrl: './bulk-resend-modal.component.html',
  styleUrls: ['./bulk-resend-modal.component.css']
})
export class BulkResendModalComponent {
  // Raw JSON string of form entry ids entered by the admin
  idsJson: string = '';

  // Delivery mode for the bulk run
  mode: BulkResendMode = 'pdf';

  // Validation message for the ids input
  parseError: string = '';

  // Per-entry progress tracking for the current/last run
  items: BulkResendItem[] = [];

  // True while a bulk run is in progress
  isRunning: boolean = false;

  // True once a run has been started (switches the view to the progress stage)
  hasRun: boolean = false;

  // Set when the admin asks to stop after the current entry
  cancelRequested: boolean = false;

  constructor(
    private modalController: ModalController,
    private formService: FormService,
    private emailService: EmailService,
    private formRendererService: FormRendererService,
    private toastService: ToastService
  ) {}

  // Progress counters for the template
  get processedCount(): number {
    return this.items.filter(i => i.status === 'success' || i.status === 'failed').length;
  }

  get successCount(): number {
    return this.items.filter(i => i.status === 'success').length;
  }

  get failedCount(): number {
    return this.items.filter(i => i.status === 'failed').length;
  }

  get remainingCount(): number {
    return this.items.length - this.processedCount;
  }

  get progressValue(): number {
    return this.items.length ? this.processedCount / this.items.length : 0;
  }

  get failedIds(): string[] {
    return this.items.filter(i => i.status === 'failed').map(i => i.id);
  }

  /**
   * Closes the modal. Blocked while a run is in progress so entries
   * are not silently abandoned mid-send.
   */
  close(): void {
    if (this.isRunning) {
      return;
    }
    this.modalController.dismiss();
  }

  /**
   * Requests cancellation of the bulk run.
   * The entry currently being sent finishes; remaining entries are skipped.
   */
  requestCancel(): void {
    this.cancelRequested = true;
  }

  /**
   * Returns to the input stage so the admin can start another run.
   */
  reset(): void {
    if (this.isRunning) {
      return;
    }
    this.hasRun = false;
    this.items = [];
    this.parseError = '';
  }

  /**
   * Copies the ids of failed entries to the clipboard as a JSON array
   * so the admin can rerun just the failures.
   */
  async copyFailedIds(): Promise<void> {
    try {
      await navigator.clipboard.writeText(JSON.stringify(this.failedIds));
      this.toastService.showSuccessMessage('Failed ids copied to clipboard');
    } catch {
      this.toastService.showErrorMessage('Failed to copy ids to clipboard');
    }
  }

  /**
   * Validates the JSON ids input and runs the bulk resend,
   * processing entries strictly one at a time.
   */
  async start(): Promise<void> {
    const ids = this.parseIds();
    if (!ids) {
      return;
    }

    this.items = ids.map(id => ({ id, status: 'pending' as BulkItemStatus }));
    this.hasRun = true;
    this.isRunning = true;
    this.cancelRequested = false;

    for (const item of this.items) {
      if (this.cancelRequested) {
        break;
      }

      item.status = 'processing';
      try {
        await this.resendForEntry(item.id);
        item.status = 'success';
      } catch (error: any) {
        console.error(`Bulk resend failed for ${item.id}:`, error);
        item.status = 'failed';
        item.error = error?.error?.message || error?.message || 'Failed to send';
      }
    }

    this.isRunning = false;

    const summary = `Bulk resend finished: ${this.successCount} sent, ${this.failedCount} failed`;
    if (this.failedCount > 0) {
      this.toastService.showWarningMessage(summary);
    } else if (this.successCount > 0) {
      this.toastService.showSuccessMessage(summary);
    }
  }

  /**
   * Parses the ids input leniently. Accepts a plain list of ids separated by
   * commas or newlines, with or without quotes and brackets, and also accepts
   * a proper JSON array. Smart quotes from copy/paste are normalized.
   * Sets parseError and returns null when no ids can be extracted.
   */
  private parseIds(): string[] | null {
    this.parseError = '';

    // Normalize smart quotes that editors and chat apps insert on paste
    const raw = (this.idsJson || '')
      .replace(/[“”„]/g, '"')
      .replace(/[‘’]/g, "'")
      .trim();

    if (!raw) {
      this.parseError = 'Enter at least one form entry id';
      return null;
    }

    let values: string[] | null = null;

    // Try JSON first for inputs that are already a valid array
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        values = parsed.map(value => String(value));
      }
    } catch {
      // Not JSON - fall through to plain-list parsing
    }

    // Plain list: strip surrounding brackets, split on commas/newlines, strip quotes
    if (!values) {
      values = raw
        .replace(/^\[/, '')
        .replace(/\]$/, '')
        .split(/[\n,]+/)
        .map(value => value.trim().replace(/^["']+/, '').replace(/["']+$/, ''));
    }

    const ids = values
      .map(value => value.trim())
      .filter(value => value.length > 0);
    const uniqueIds = [...new Set(ids)];

    if (uniqueIds.length === 0) {
      this.parseError = 'Enter at least one form entry id';
      return null;
    }

    return uniqueIds;
  }

  /**
   * Resends the email for a single form entry: fetches the entry,
   * generates its HTML, categorizes attachments, and calls the endpoint
   * matching the selected delivery mode.
   */
  private async resendForEntry(formEntryId: string): Promise<void> {
    // Fetch the complete form entry with resolved attachments
    const wrapper = await firstValueFrom(this.formService.getFormEntryById<any>(formEntryId));
    const response = wrapper?.response;
    if (!response?.formDoc?.formEntryData) {
      throw new Error('Form entry not found');
    }

    // Resolve the form model and metadata, mirroring the form view page
    const formType = this.normalizeFormType(formEntryId.split('-')[0]);
    const ModelClass = getFormModel(formType);
    if (!ModelClass) {
      throw new Error(`Unknown form type: ${formType}`);
    }

    const modelInstance = new ModelClass(response.formDoc.formEntryData);
    const formInfo = ModelClass.getFormInfo();
    const fieldLabels = ModelClass.getFieldMappings();
    const formSteps = typeof ModelClass.getFormSteps === 'function'
      ? ModelClass.getFormSteps()
      : (formInfo.steps || []);
    const fieldMetadata = FORM_REGISTRY[formType]?.fieldMetadata || {};

    // Generate the form HTML in placeholder mode (attachments are referenced by blob name)
    const htmlContent = await this.formRendererService.generateFormHTMLWithoutAttachments({
      formEntry: modelInstance,
      formSteps,
      fieldLabels,
      formTitle: formInfo.formTitle,
      fieldMetadata,
      wordPressFields: response.formDoc.wordPressFields || []
    });
    if (!htmlContent || !htmlContent.trim()) {
      throw new Error('Failed to generate form content');
    }

    const attachmentData = this.categorizeAttachments(response.attachments);

    const emailTemplateData = {
      submittedByName: modelInstance.submittedByName || 'Unknown',
      submittedByEmail: modelInstance.submittedByEmail || '',
      submissionDate: this.formatDate(modelInstance.submissionDate),
      specifiedRecipient: modelInstance.recipientEmail || undefined
    };

    if (this.mode === 'html') {
      const request: ManualEmailHtmlRequest = {
        formType,
        recipients: [], // Empty array means use standard recipients
        emailTemplateData,
        htmlContent,
        imageBlobNames: attachmentData.imageBlobNames,
        pdfBlobNames: attachmentData.pdfBlobNames
      };
      await firstValueFrom(this.emailService.sendEmailHtmlRequest(request));
    } else {
      const request: ManualEmailWithPdfRequest = {
        formType,
        formId: formEntryId,
        recipients: [], // Empty array means use standard recipients
        emailTemplateData,
        htmlContent,
        imageBlobNames: attachmentData.imageBlobNames,
        pdfBlobNames: attachmentData.pdfBlobNames
      };
      await firstValueFrom(this.emailService.sendEmailGeneratePdf(request));
    }
  }

  /**
   * Categorizes upload and signature attachment blobs into images and PDFs.
   */
  private categorizeAttachments(attachments: ResolvedAttachments | undefined): { imageBlobNames: string[]; pdfBlobNames: string[] } {
    const result = {
      imageBlobNames: [] as string[],
      pdfBlobNames: [] as string[]
    };

    const sections: Record<string, AttachmentWithUrl[] | undefined>[] = [
      attachments?.uploads || {},
      attachments?.signatures || {}
    ];

    sections.forEach(section => {
      Object.values(section).forEach(sectionAttachments => {
        sectionAttachments?.forEach(({ blobName, contentType, fileName }) => {
          if (isImage(contentType, fileName)) {
            result.imageBlobNames.push(blobName);
          } else if (isPdf(contentType, fileName)) {
            result.pdfBlobNames.push(blobName);
          }
        });
      });
    });

    return result;
  }

  /**
   * Normalizes form type strings to kebab-case, matching the form view page.
   */
  private normalizeFormType(formType: string): string {
    return formType
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .replace(/\s+/g, '-')
      .replace(/_/g, '-')
      .toLowerCase();
  }

  /**
   * Formats a date as yyyy-MM-dd for the email template data.
   */
  private formatDate(dateString: string | Date | undefined): string {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
